/**
 * Shared toast provider — call `useToast().push("ok", "Added gauge")`
 * from anywhere to surface a 3-second feedback message.
 *
 * Lifted from the `CanvasToast` pattern that lived inside
 * `apps/web/src/foundry-widgets/ProcessGraphCanvas.tsx` (B0.27).
 * Promoting to app-level lets every store mutation (B0.42 dashboards,
 * alarms, tags), every copilot apply (B0.43), and any future surface
 * use one consistent feedback channel.
 *
 * Mount at the app root in `App.tsx`. The provider renders its own
 * stack overlay at the bottom-centre of the viewport — no consumer
 * needs to render anything to use it.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { cn } from "./cn";

// "warn" is for an action that SUCCEEDED but produced a state the user needs to fix.
// Without it there was no honest way to say so: "error" claims something failed when it
// did not, and "info" understates a configuration that will silently drop data.
export type ToastKind = "ok" | "error" | "info" | "warn";

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  /** Optional secondary action: a label + onClick (used for "Undo" buttons). */
  action?: { label: string; onClick: () => void };
}

export interface ToastApi {
  push: (
    kind: ToastKind,
    message: string,
    options?: { action?: ToastItem["action"]; durationMs?: number },
  ) => number;
  dismiss: (id: number) => void;
  clear: () => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const ICONS: Record<ToastKind, typeof CheckCircle2> = {
  ok: CheckCircle2,
  error: AlertTriangle,
  info: Info,
  warn: AlertTriangle,
};

const TONE: Record<ToastKind, string> = {
  ok: "border-success/40 bg-success/10 text-success",
  error: "border-danger/40 bg-danger/10 text-danger",
  info: "border-info/40 bg-info/10 text-info",
  warn: "border-warning/40 bg-warning/10 text-warning",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback<ToastApi["push"]>(
    (kind, message, options) => {
      const id = Date.now() + Math.random();
      const item: ToastItem = { id, kind, message, action: options?.action };
      setItems((prev) => [...prev, item]);
      const dur = options?.durationMs ?? 3500;
      if (dur > 0) {
        setTimeout(() => dismiss(id), dur);
      }
      return id;
    },
    [dismiss],
  );

  const clear = useCallback(() => setItems([]), []);

  const api = useMemo<ToastApi>(
    () => ({ push, dismiss, clear }),
    [push, dismiss, clear],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastOverlay items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}

/**
 * Hook-friendly variant that returns a no-op API when the provider isn't
 * mounted — useful from libraries that should still work in isolation
 * (tests, /app/_widgets preview route).
 */
export function useOptionalToast(): ToastApi {
  const ctx = useContext(ToastContext);
  return ctx ?? NOOP_API;
}

const NOOP_API: ToastApi = {
  push: () => 0,
  dismiss: () => {},
  clear: () => {},
};

function ToastOverlay({
  items,
  onDismiss,
}: {
  items: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2"
      role="region"
      aria-live="polite"
      aria-label="Notifications"
    >
      {items.map((t) => {
        const Icon = ICONS[t.kind];
        return (
          <div
            key={t.id}
            className={cn(
              "pointer-events-auto flex max-w-md items-center gap-2 rounded-md border px-3 py-1.5 text-xs shadow-md backdrop-blur",
              TONE[t.kind],
            )}
          >
            <Icon className="size-3.5 shrink-0" />
            <span className="flex-1 text-on-surface">{t.message}</span>
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  t.action!.onClick();
                  onDismiss(t.id);
                }}
                className="rounded border border-current px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide hover:bg-current/10"
              >
                {t.action.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => onDismiss(t.id)}
              className="rounded p-0.5 hover:bg-current/10"
              aria-label="Dismiss"
            >
              <X className="size-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
